import * as preact from "preact";

import readlangClient from "readlangClient";
import * as experimentUtils from "shared/experimentUtils";
import * as languageMap from "shared/languageMap";

type LanguageCount = {
  language: string;
  count: number;
};

type UserStats = {
  user: {
    username: string;
    displayName?: string;
    subscriptionPlan: string;
    subscriptionExpiryDate: string | null;
    /** Derived server-side from the bound Stripe Subscription; 0 = no renewal. */
    renewalMonths: number;
    currentLearningLanguage?: string;
    firstLanguage?: string;
  } | null;
  userWordCounts: LanguageCount[];
  userBookCounts: LanguageCount[];
  /** Condition per running experiment the user has been treated in. */
  experimentTreatments: { [experiment: string]: string };
};

type UsageBucket = {
  label: string;
  count: number;
};

type UsageSummary = {
  daily: UsageBucket[];
  monthly: UsageBucket[];
  yearly: UsageBucket[];
};

type Props = {
  userID: string;
};

type State = {
  loading: boolean;
  stats: UserStats | null;
  error: string | null;
  usageSummary: UsageSummary | null;
  usageSummaryLoading: boolean;
  usageSummaryError: string | null;
};

export default class UserStatsView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      stats: null,
      error: null,
      usageSummary: null,
      usageSummaryLoading: true,
      usageSummaryError: null,
    };
  }

  componentDidMount() {
    this.fetchStats();
    this.fetchUsageSummary();
  }

  componentDidUpdate(previousProps: Props) {
    if (previousProps.userID !== this.props.userID) {
      this.fetchStats();
      this.fetchUsageSummary();
    }
  }

  async fetchStats() {
    this.setState({ loading: true, error: null });
    try {
      const response = await readlangClient.request(
        "GET",
        `/api/admin/user/${this.props.userID}/stats`,
        {}
      );
      this.setState({ stats: response.data, loading: false });
    } catch (error) {
      this.setState({ error: "Failed to fetch user stats", loading: false });
    }
  }

  async fetchUsageSummary() {
    this.setState({ usageSummaryLoading: true, usageSummaryError: null });
    try {
      const response = await readlangClient.request(
        "GET",
        `/api/admin/user/${this.props.userID}/usageSummary`,
        {}
      );
      this.setState({
        usageSummary: response.data,
        usageSummaryLoading: false,
      });
    } catch (error) {
      this.setState({
        usageSummaryError: "Failed to fetch usage summary",
        usageSummaryLoading: false,
      });
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) {
      return "N/A";
    }
    return new Date(dateString).toLocaleDateString();
  }

  getLanguageName(code: string): string {
    const entry = languageMap.map[code];
    return entry?.name || code;
  }

  renderUsageBuckets(buckets: UsageBucket[], title: string) {
    return (
      <div>
        <h5>{title}</h5>
        <table class="infoTable">
          <tbody>
            {buckets.map((bucket) => (
              <tr key={bucket.label}>
                <th>{bucket.label}</th>
                <td>{bucket.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderUsageSummary() {
    const { usageSummary, usageSummaryLoading, usageSummaryError } = this.state;

    if (usageSummaryLoading) {
      return <p>Loading usage summary...</p>;
    }

    if (usageSummaryError) {
      return <p class="error">{usageSummaryError}</p>;
    }

    if (!usageSummary) {
      return null;
    }

    return (
      <div>
        <h4>Usage Summary (Learning Logs)</h4>
        {this.renderUsageBuckets(usageSummary.daily, "Daily (Past 7 Days)")}
        {this.renderUsageBuckets(
          usageSummary.monthly,
          "Monthly (Past 12 Months)"
        )}
        {this.renderUsageBuckets(usageSummary.yearly, "Yearly (All Time)")}
      </div>
    );
  }

  renderExperiments(experimentTreatments: UserStats["experimentTreatments"]) {
    return (
      <table class="infoTable">
        <tbody>
          {experimentUtils.runningExperiments.map((experiment) => {
            const { condition, override } = experimentUtils.getCondition(
              this.props.userID,
              experiment
            );
            return (
              <tr key={experiment}>
                <th>{experiment}</th>
                <td>
                  {experimentTreatments[experiment] ??
                    `Not treated${override ? ` (pinned to ${condition})` : ""}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  renderLanguageCounts(counts: LanguageCount[], title: string) {
    if (counts.length === 0) {
      return <p class="emptyText">No {title.toLowerCase()}</p>;
    }
    return (
      <table class="infoTable">
        <tbody>
          {counts.map((item) => (
            <tr key={item.language}>
              <th>{this.getLanguageName(item.language)}</th>
              <td>{item.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    const { loading, stats, error } = this.state;

    if (loading) {
      return (
        <div class="userStatsView">
          <p>Loading stats...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div class="userStatsView">
          <p class="error">{error}</p>
        </div>
      );
    }

    if (!stats || !stats.user) {
      return (
        <div class="userStatsView">
          <p>No stats available</p>
        </div>
      );
    }

    const { user, userWordCounts, userBookCounts } = stats;

    return (
      <div class="userStatsView">
        <table class="infoTable">
          <tbody>
            <tr>
              <th>Display Name</th>
              <td>{user.displayName || user.username || "N/A"}</td>
            </tr>
            <tr>
              <th>Subscription Plan</th>
              <td>{user.subscriptionPlan || "free"}</td>
            </tr>
            <tr>
              <th>Expiry Date</th>
              <td>{this.formatDate(user.subscriptionExpiryDate)}</td>
            </tr>
            <tr>
              <th>Renewal Months</th>
              <td>{user.renewalMonths || "does not renew"}</td>
            </tr>
            <tr>
              <th>Learning</th>
              <td>
                {user.currentLearningLanguage && user.firstLanguage
                  ? `${languageMap.map[user.currentLearningLanguage]?.english ?? user.currentLearningLanguage} from ${languageMap.map[user.firstLanguage]?.english ?? user.firstLanguage}`
                  : "N/A"}
              </td>
            </tr>
          </tbody>
        </table>

        <h4>Experiments</h4>
        {this.renderExperiments(stats.experimentTreatments)}

        <h4>User Words by Language</h4>
        {this.renderLanguageCounts(userWordCounts, "User Words")}

        <h4>User Books by Language</h4>
        {this.renderLanguageCounts(userBookCounts, "User Books")}

        {this.renderUsageSummary()}
      </div>
    );
  }
}
